import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import "../../../forms-common.scss";
import PageLayout from "../../_layout/PageLayout";
import {
  CreateJob,
  DeleteJob,
  ViewJobsList,
  ViewJobsFromAssignedProject,
  ManageDeletedData,
} from "../../../services/authorization/Permissions";

import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import AddNewButton from "../../../components/buttons/add-new-button/AddNewButton";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { JobsStorageModel } from "../../../services/job/JobsStorage";
import { api } from "../../../services/api/ApiService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import DataGrid from "../../../components/datatable/DataGrid";
import { ChangeJobStatusButton } from "../components/buttons/ChangeJobStatusButton";
import { ChangeJobPriorityButton } from "../components/buttons/ChangeJobPriorityButton";
import { AssignJobToUserButton } from "../components/buttons/AssignJobToUserButton";
import { AddJobToProjectButton } from "../components/buttons/AddJobToProjectButton";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [jobsStorage] = useState(() =>
    JobsStorageModel.create({ useGridFiltering: true }, { api })
  );

  const selectedJobsCount = useMemo(
    () => jobsStorage.selectedItemsCount,
    [jobsStorage.selectedItemsCount]
  );

  const isAddJobDisabled = useMemo(() => !permissionsService.userHasPermissionTo(CreateJob), []);
  const isDeleteDisabled = useMemo(
    () => !permissionsService.userHasPermissionTo(DeleteJob) || !jobsStorage.anyItemsSelected,
    [jobsStorage.anyItemsSelected]
  );

  const deleteSelectedJobs = useCallback(async () => {
    const selectedJobsCount = jobsStorage.selectedItemsCount;
    try {
      await jobsStorage.deleteJobs(jobsStorage.selectedItems);
      snackbarNotificationsService.showSuccess("JobsPage.delete.success", { selectedJobsCount });
    } catch (error) {
      const errorContext = { selectedJobsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.jobs.delete");
      handleErrorInSnackbar(applicationError);
    }
  }, [jobsStorage, handleErrorInSnackbar]);

  useEffect(() => {
    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewJobsList, ViewJobsFromAssignedProject]}
      titleKey="general.jobs"
    >
      <ActionsSection>
        <ButtonsSection>
          <AddNewButton
            disabled={isAddJobDisabled}
            onClick={() => history.push("/jobs/create-job")}
          />
          <ChangeJobStatusButton jobsStorage={jobsStorage} />
          <ChangeJobPriorityButton jobsStorage={jobsStorage} />
          <AssignJobToUserButton jobsStorage={jobsStorage} />
          <AddJobToProjectButton jobsStorage={jobsStorage} />
          <DeleteButton
            disabled={isDeleteDisabled}
            confirmationAction={deleteSelectedJobs}
            confirmationModalTitleKey="JobsPage.delete.confirmation.title"
            confirmationModalTitleContext={{ jobsCount: selectedJobsCount }}
            confirmationModalSubtitleKey="JobsPage.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid itemTypeKey="general.jobs" rowClickPath="/jobs/:id" dataGridStorage={jobsStorage} />
    </PageLayout>
  );
});
